import React, {
  MouseEvent,
  Suspense,
  useEffect,
  useRef,
  Dispatch,
  useCallback,
  PointerEvent,
  useMemo,
} from 'react';
import { Box, Button, Divider, Checkbox, SxProps, Theme } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SEARCH, VIEW_ARTICLE, VIEW_DRAFT } from 'constants/routes';
import LightboxModal from 'components/common/modal/LightboxModal';
import Loader from 'components/common/loader/Loader';
import { ArticleUserMapType, CommentType } from 'pages/article/hooks/useArticleDetailsQuery';
import { ProjectRecommendationsType, ProjectType } from 'pages/projects/hooks/useProjectsQuery';
import { ArticleSourceType, ArticleType } from '../hooks/useArticlesQuery';
import RenderAttachments from './RenderAttachments';
import CommentsBox from 'pages/article/components/CommentsBox';
import useArticle from '../hooks/useArticle';
import AutoGeneratedDraftMessage from './AutoGeneratedDraftMessage';
import ArticleInteractions, {
  ArticleTypeLabel,
  ArticleTypeLabelContainer,
} from './ArticleInteractions';
import ArticleModals from './ArticleModals';
import RenderPlaylists from './RenderPlaylists';
import PlaylistBadge from './PlaylistBadge';
import ArticleMenu from './ArticleMenu';
import ArticleHeader from './ArticleHeader';
import DraftActions from './DraftActions';
import Attachments from './Attachments';
import { ArticleAttachmentsSummary } from '@buildwise/pages/article/hooks/useArticleAttachmentsSummary';
import { FeedFilter } from 'api/KnowledgeAPI';
import {
  PostAttachmentsCountWithBanner,
  PostDescription,
  PostInfo,
  PostTitle,
  RenderArticleTypeLabel,
} from '@buildwise/libs/posts';
import { ConfigEntryType } from 'config/config';
import { QuestionAskedBy } from '@buildwise/libs/q&a';
import ChatQuoteFillIcon from 'remixicon-react/ChatQuoteFillIcon';
import QuestionFillIcon from 'remixicon-react/QuestionFillIcon';
import FileListFillIcon from 'remixicon-react/FileListFillIcon';
import { ReactComponent as RetrospectiveFillIcon } from 'assets/icons/RetrospectiveFillIcon.svg';
import useUserData from 'hooks/useUserData';
import { useHelpCenter } from '@buildwise/libs/help-center/hooks/useHelpCenter';

export type ArticleVariantType =
  | 'detailed'
  | 'short'
  | 'draft'
  | 'list'
  | 'related-post'
  | 'playlist'
  | 'playlist-article';

type ArticlePropTypes = {
  article: ArticleType;
  articleAttachmentsSummary?: ArticleAttachmentsSummary;
  articleFields?: ConfigEntryType[];
  articleIndex?: number;
  comments?: CommentType[];
  contentSx?: SxProps<Theme>;
  doNotAutoplayVideos?: boolean;
  isRecommendedType?: boolean;
  project?: ProjectType | ProjectRecommendationsType;
  scrollInfoKey?: string;
  showAttachmentSummaryIndicatorSkeleton?: boolean;
  stakeholderId?: string;
  selectedFeedFilters?: FeedFilter[];
  sx?: SxProps<Theme>;
  userMap?: ArticleUserMapType;
  variant?: ArticleVariantType;
  bookmarkUpdateCallback?: (options: {
    oldArticle: ArticleType;
    updatedArticle: ArticleType;
  }) => void;
  deleteArticleCallback?: (options: { article: ArticleType }) => void;
  endorseArticleCallback?: (options: {
    oldArticle: ArticleType;
    updatedArticle: ArticleType;
  }) => void;
  likeUpdateCallback?: (options: { oldArticle: ArticleType; updatedArticle: ArticleType }) => void;
  onEditArticleCallback?: () => void;
  resetFeed?: (options?: {
    resetQueries?: boolean | undefined;
    smoothScrollBehavior?: boolean | undefined;
  }) => void;
  updateArticleCallback?: (options: {
    oldArticle: ArticleType;
    updatedArticle: ArticleType;
  }) => void;
  showCheckbox?: boolean;
  articlePreviewInfo?: ArticleType[];
  setArticlePreviewInfo?: Dispatch<React.SetStateAction<ArticleType[]>>;
  isPreviewLoading?: boolean;
  isSME?: boolean;
};

const ArticleBox: React.FC<ArticlePropTypes> = ({
  article,
  articleAttachmentsSummary,
  articleFields = [],
  articleIndex,
  comments = [],
  contentSx,
  doNotAutoplayVideos = false,
  isRecommendedType = false,
  project,
  scrollInfoKey,
  showAttachmentSummaryIndicatorSkeleton,
  stakeholderId,
  selectedFeedFilters,
  sx,
  userMap = {},
  variant = 'detailed',
  bookmarkUpdateCallback,
  deleteArticleCallback,
  endorseArticleCallback,
  likeUpdateCallback,
  onEditArticleCallback,
  resetFeed,
  updateArticleCallback,
  showCheckbox,
  articlePreviewInfo,
  setArticlePreviewInfo,
  isPreviewLoading,
  isSME,
}) => {
  const { isHelpCenterArticle, isAdminHelpCenterArticle, disableAction } = useHelpCenter({
    article,
  });
  const theme = useTheme();
  const articleDescriptionRef = useRef<HTMLParagraphElement | null>(null);
  const {
    articleState,
    autoGeneratedDraftMenu,
    documents,
    hasEditAccess,
    isAutoGeneratedDraft,
    isDetailedView,
    isDraftView,
    isListView,
    isPublishedByBuildWiseInsights,
    isRelatedPostView,
    isShortView,
    mediaFiles,
    menu,
    pathname,
    routeState,
    searchKeyword,
    selectedCompanyIds,
    bookmarkHandler,
    deleteArticle,
    dispatch,
    endorseHandler,
    likeHandler,
    movePost,
    moveToDrafts,
    openInsightsBanner,
    publishArticle,
    setSelectedCompanyIds,
    viewInteractionsHandler,
  } = useArticle({
    article,
    articleIndex,
    project,
    selectedFeedFilters,
    stakeholderId,
    variant,
    bookmarkUpdateCallback,
    deleteArticleCallback,
    endorseArticleCallback,
    likeUpdateCallback,
    onEditArticleCallback,
    resetFeed,
    updateArticleCallback,
  });
  const navigate = useNavigate();

  const url = useMemo(() => {
    const queryParams = new URLSearchParams();

    if (searchKeyword) queryParams.append('q', encodeURIComponent(searchKeyword));
    if (isAdminHelpCenterArticle) queryParams.append('isAdminHelpCenterArticle', 'true');

    const queryString = queryParams.toString();
    return `${VIEW_ARTICLE.replace(':articleId', article.id)}${queryString ? `?${queryString}` : ''}`;
  }, [searchKeyword, isAdminHelpCenterArticle, article]);

  const handleEmbeddedImageClick = useCallback((event: unknown) => {
    const attachmentId =
      (event as MouseEvent)?.currentTarget?.getAttribute('data-attachment-id') ||
      (event as MouseEvent)?.currentTarget?.getAttribute('attachmentid');
    const fileName = mediaFiles.find((file) => file.id === attachmentId)?.fileName;

    if (fileName) {
      dispatch({
        type: 'TOGGLE_LIGHTBOX',
        payload: { open: true, fileName },
      });
    }
  }, []);

  const handleHashtagClick = useCallback((event: unknown) => {
    const hashtag = ((event as PointerEvent)?.target as HTMLSpanElement)?.textContent || '';
    if (hashtag) {
      navigate(`${SEARCH}?q=${encodeURIComponent(`${hashtag}`)}`);
    }
  }, []);

  useEffect(() => {
    if (articleDescriptionRef.current) {
      // Add click event for embedded images
      const embeddedImages = articleDescriptionRef.current.querySelectorAll('img');

      embeddedImages.forEach((embeddedImage) => {
        embeddedImage.style.cursor = 'pointer';
        embeddedImage.addEventListener('click', handleEmbeddedImageClick);
      });

      // Add click event for hashtags
      const hashtags = articleDescriptionRef.current.querySelectorAll('span.hashtag');

      hashtags.forEach((hashtag) => {
        hashtag.addEventListener('click', handleHashtagClick);
      });
    }

    return () => {
      if (articleDescriptionRef.current) {
        // Remove click event for embedded images
        const embeddedImages = articleDescriptionRef.current.querySelectorAll('img');

        embeddedImages.forEach((embeddedImage) =>
          embeddedImage.removeEventListener('click', handleEmbeddedImageClick),
        );

        // Remove click event for hashtags
        const hashtags = articleDescriptionRef.current.querySelectorAll('span.hashtag');

        hashtags.forEach((hashtag) => {
          hashtag.removeEventListener('click', handleHashtagClick);
        });
      }
    };
  }, [articleDescriptionRef, article.description]);

  return (
    <>
      {isDetailedView && article.isDraft && (
        <DraftActions
          article={article}
          articleState={articleState}
          autoGeneratedDraftMenu={autoGeneratedDraftMenu}
          dispatch={dispatch}
        />
      )}

      <ArticleContainer
        className={`article-container ${variant}`}
        sx={{ ...sx, padding: isHelpCenterArticle ? '12px 0' : 0 }}
      >
        {article.isDraft && (
          <AutoGeneratedDraftMessage
            dispatch={dispatch}
            variant={variant}
            draft={article}
            userMap={userMap}
            isAutoGeneratedDraft={!!isAutoGeneratedDraft}
          />
        )}

        {isRecommendedType && (
          <ArticleHeader
            variant={variant}
            article={article}
            project={project as ProjectRecommendationsType}
            dispatch={dispatch}
          />
        )}

        <ArticleContentContainer className={`article-box ${variant}`} sx={contentSx}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              {showCheckbox && (
                <Checkbox
                  onChange={() => {
                    setArticlePreviewInfo?.((prevState) =>
                      prevState?.find((item) => item?.id === article?.id)
                        ? prevState.filter((item) => item?.id !== article?.id)
                        : [...prevState, article],
                    );
                  }}
                  checked={!!articlePreviewInfo?.find((item) => item?.id === article?.id)}
                  disabled={isPreviewLoading}
                  sx={{
                    ml: -1,
                  }}
                />
              )}
              {!isDetailedView &&
                article.originIndicator !== 'PLAYLIST' &&
                (article.articleType === ArticleSourceType.QUESTION ? (
                  <ArticleTypeLabelContainer>
                    <QuestionFillIcon color={theme.palette.custom.dodgerBlue} size={18} />
                    <ArticleTypeLabel>Question</ArticleTypeLabel>
                  </ArticleTypeLabelContainer>
                ) : article.articleType === ArticleSourceType.SUGGESTION ? (
                  <ArticleTypeLabelContainer>
                    <ChatQuoteFillIcon color={theme.palette.custom.cloverGreen} size={18} />
                    <ArticleTypeLabel>Suggestion</ArticleTypeLabel>
                  </ArticleTypeLabelContainer>
                ) : article.articleType === ArticleSourceType.LESSON ? (
                  <ArticleTypeLabelContainer
                    sx={{ '& svg path': { fill: theme.palette.custom.lavenderIndigo } }}
                  >
                    <RetrospectiveFillIcon width={18} />
                    <ArticleTypeLabel>Learning</ArticleTypeLabel>
                  </ArticleTypeLabelContainer>
                ) : (
                  <ArticleTypeLabelContainer>
                    <FileListFillIcon color={theme.palette.custom.pirateGold} size={18} />
                    <ArticleTypeLabel>Post</ArticleTypeLabel>
                  </ArticleTypeLabelContainer>
                ))}
              {isDetailedView && article.originIndicator !== 'PLAYLIST' && !isHelpCenterArticle && (
                <RenderArticleTypeLabel article={article} />
              )}
            </Box>
            {!isRelatedPostView && !disableAction && (
              <ArticleUserContainer className={variant}>
                <ArticleMenu menu={menu} bookmarkHandler={bookmarkHandler} article={article} />
              </ArticleUserContainer>
            )}
          </Box>

          {!isRelatedPostView && (
            <ArticleContent
              className={variant}
              sx={{
                '& img': {
                  ...(!isDetailedView && { width: '100% !important', height: '100% !important' }),
                },
                ...(!isHelpCenterArticle && {
                  overflow: 'hidden',
                }),
              }}
            >
              {!isDraftView && (
                <Box
                  sx={{
                    cursor: isShortView || isListView || isDraftView ? 'pointer' : 'auto',
                  }}
                  {...((isShortView || isDraftView || isListView) && {
                    component: Link,
                    to: url,
                    state: {
                      isPlayListFeature: article.originIndicator === 'PLAYLIST',
                      scrollInfoKey,
                      activeSidebarTab: pathname.startsWith('/admin/bookmarks') ? 'Bookmarks' : '',
                      article,
                    },
                  })}
                >
                  <Box>
                    {!isDetailedView && (
                      <PostTitle
                        searchKeyword={searchKeyword}
                        title={article.title}
                        variant={variant}
                      />
                    )}

                    {!isDetailedView && (
                      <PostDescription
                        description={
                          isDetailedView
                            ? article?.description
                            : article?.description?.slice(0, 5000)?.replaceAll('&nbsp;', '')
                        }
                        isDetailedView={isDetailedView}
                        isRelatedPostView={isRelatedPostView}
                        ref={articleDescriptionRef}
                        searchKeyword={searchKeyword}
                        variant={variant}
                      />
                    )}

                    {(isShortView || isListView) && article.originIndicator === 'PLAYLIST' && (
                      <PlaylistBadge article={article} />
                    )}

                    {(isShortView || isListView) && mediaFiles.length > 0 && (
                      <Attachments
                        attachments={mediaFiles}
                        article={article}
                        doNotAutoplayVideos
                        variant={variant}
                      />
                    )}

                    {!isDetailedView && documents.length > 0 && (
                      <PostAttachmentsCountWithBanner
                        article={article}
                        documents={documents}
                        showAttachmentSummaryIndicatorSkeleton={
                          showAttachmentSummaryIndicatorSkeleton
                        }
                      />
                    )}
                  </Box>
                </Box>
              )}

              {isDetailedView && (
                <>
                  <PostInfo
                    article={article}
                    articleAttachmentsSummary={articleAttachmentsSummary}
                    articleFields={articleFields}
                    documents={documents}
                    isDetailedView={isDetailedView}
                    isRelatedPostView={isRelatedPostView}
                    mediaFiles={mediaFiles}
                    ref={articleDescriptionRef}
                    searchKeyword={searchKeyword}
                    variant={variant}
                    dispatch={dispatch}
                  />
                  {article.originIndicator === 'QUESTION_AND_ANSWER' && (
                    <QuestionAskedBy article={article} />
                  )}
                </>
              )}
            </ArticleContent>
          )}

          {(isDraftView || isRelatedPostView) && (
            <Link
              to={url}
              state={{
                isPlayListFeature: article.originIndicator === 'PLAYLIST',
                scrollInfoKey,
                isDraft: article?.isDraft,
                article,
              }}
            >
              <DraftContentContainer className={variant}>
                <Box flex={1} marginRight={mediaFiles.length > 0 ? 1 : 0}>
                  <PostTitle
                    searchKeyword={searchKeyword}
                    title={article.title}
                    variant={variant}
                  />
                  <PostDescription
                    description={
                      isDetailedView
                        ? article?.description
                        : article?.description?.slice(0, 5000)?.replaceAll('&nbsp;', '')
                    }
                    isDetailedView={isDetailedView}
                    isRelatedPostView={isRelatedPostView}
                    ref={articleDescriptionRef}
                    searchKeyword={searchKeyword}
                    variant={variant}
                  />
                </Box>
                {mediaFiles.length > 0 && (
                  <RenderAttachments
                    {...{
                      attachments: mediaFiles,
                      doNotAutoplayVideos,
                      variant,
                      isShortView,
                      isDetailedView,
                      isDraftView,
                      isRelatedPostView,
                      article,
                    }}
                  />
                )}
              </DraftContentContainer>
            </Link>
          )}

          {!(isDetailedView && article.originIndicator === 'PLAYLIST') && !isHelpCenterArticle && (
            <ArticleInteractions
              article={article}
              dispatch={dispatch}
              isDetailedView={isDetailedView}
              isDraftView={isDraftView}
              isRelatedPostView={isRelatedPostView}
              likeHandler={likeHandler}
              scrollInfoKey={scrollInfoKey}
              searchKeyword={searchKeyword}
              variant={variant}
              viewInteractionsHandler={viewInteractionsHandler}
              userMap={userMap}
              isPublishedByBuildWiseInsights={isPublishedByBuildWiseInsights}
              openInsightsBanner={openInsightsBanner}
            />
          )}
        </ArticleContentContainer>

        {isDetailedView && article.originIndicator === 'PLAYLIST' && (
          <>
            <Box px={3}>
              <PlaylistBadge
                article={article}
                hasEditAccess={hasEditAccess}
                isDetailedView={isDetailedView}
                dispatch={dispatch}
                disableAction={disableAction}
              />
            </Box>
            <RenderPlaylists article={article} dispatch={dispatch} disableAction={disableAction} />
            {!isHelpCenterArticle && (
              <Box px={3}>
                <ArticleInteractions
                  article={article}
                  dispatch={dispatch}
                  isDetailedView={isDetailedView}
                  isDraftView={isDraftView}
                  isRelatedPostView={isRelatedPostView}
                  likeHandler={likeHandler}
                  scrollInfoKey={scrollInfoKey}
                  searchKeyword={searchKeyword}
                  variant={variant}
                  viewInteractionsHandler={viewInteractionsHandler}
                  userMap={userMap}
                  isPublishedByBuildWiseInsights={isPublishedByBuildWiseInsights}
                  openInsightsBanner={openInsightsBanner}
                />
              </Box>
            )}
          </>
        )}

        {isDetailedView && !isHelpCenterArticle && (
          <>
            <Divider sx={{ mt: 2, mx: 3 }} />
            <Box pb={2} className="article-comments">
              <CommentsBox
                articleId={article.id}
                comments={comments || []}
                article={article}
                userMap={userMap}
              />
            </Box>
          </>
        )}

        <Suspense fallback={<Loader variant="fixed" />}>
          <ArticleModals
            articleState={articleState}
            userMap={userMap}
            article={article}
            deleteArticle={deleteArticle}
            publishArticle={publishArticle}
            isDetailedView={isDetailedView}
            dispatch={dispatch}
            hasEditAccess={hasEditAccess}
            project={project as ProjectRecommendationsType}
            movePost={movePost}
            moveToDrafts={moveToDrafts}
            stakeholderId={stakeholderId}
            endorseHandler={endorseHandler}
            selectedFeedFilters={selectedFeedFilters}
            selectedCompanyIds={selectedCompanyIds}
            setSelectedCompanyIds={setSelectedCompanyIds}
            updateArticleCallback={updateArticleCallback}
          />
        </Suspense>

        {mediaFiles.length > 0 && articleState.lightbox.open && (
          <LightboxModal<ArticleType>
            onClose={() =>
              dispatch({ type: 'TOGGLE_LIGHTBOX', payload: { open: false, fileName: '' } })
            }
            open={articleState.lightbox.open}
            defaultOpenFileName={articleState.lightbox.fileName}
            mediaFiles={mediaFiles}
            item={article}
          />
        )}
      </ArticleContainer>
    </>
  );
};

export const ArticleContainer = styled(Box, { label: 'ArticleContainer' })(({ theme }) => ({
  backgroundColor: theme.palette.custom.white,
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  '&.detailed': {
    borderRadius: '16px',
  },
  '&.short, &.draft': {
    borderRadius: '8px',
    marginBottom: 8,
  },
  '&.list': {
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.custom.divider}`,
    marginBottom: 8,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  '&.related-post': {
    boxShadow: 'none',
  },
}));

const AnsweredTitle = styled(Box, { label: 'AnsweredTitle' })(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.custom.label,
  marginBottom: 8,
}));

export const ArticleContentContainer = styled(Box, { label: 'ArticleContentContainer' })({
  padding: '0 16px',
  '&.detailed': {
    padding: '0 24px',
  },
  '&.list': {
    padding: '0',
  },
  '&.related-post': {
    marginTop: 16,
  },
});

export const ArticleContent = styled(Box, { label: 'ArticleContent' })({
  '&.list': {
    paddingLeft: 0,
  },
  '& img': {
    maxWidth: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
  },
});

export const ArticleUserContainer = styled(Box, { label: 'ArticleUserContainer' })({
  flex: 1,
  padding: '16px 0 8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  '&.list': {
    justifyContent: 'flex-end',
  },
});

export const DraftContentContainer = styled(Box, { label: 'DraftContentContainer' })({
  display: 'flex',
  justifyContent: 'space-between',
  cursor: 'pointer',
  '&.related-post': {
    marginTop: 8,
  },
  '& img': {
    width: '100% !important',
    maxWidth: '100% !important',
    height: '100% !important',
    maxHeight: '100% !important',
    overflow: 'hidden',
  },
});

export const $ActionButtons = styled(Button, { label: 'ActionButtons' })(({ theme }) => ({
  color: theme.palette.secondary.dark,
  fontSize: 15,
  fontWeight: 400,
  minWidth: 40,
  height: 29,
  padding: '16px 12px 8px',
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: 5,
  },
}));

export const AvatarContainer = styled(Box, { label: 'AvatarContainer' })({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '40px',
  marginTop: '16px',
});

export const ExportButtonContainer = styled(Box, { label: 'ExportButtonContainer' })({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  paddingTop: '8px',
});

export default ArticleBox;
